import { ZodError, type z, type ZodType } from "zod";
import { reportSchemaFailure } from "_shared/_errors/client-error-reporter";

/** Reports only schema, operation, issue code, and field path—never payload or issue text. */
export function logZodError(
  err: ZodError,
  opts: {
    schemaName: string;
    operation: string;
    payloadSample: unknown;
  },
) {
  reportSchemaFailure({
    schemaName: opts.schemaName ?? "unknown",
    operation: opts.operation ?? "unknown",
    phase: "validation",
    fields: err.issues.map((issue) => {
      const path = issue.path.map(String).join(".") || "root";
      return `${path}:${issue.code}`;
    }),
  });
}

/**
 * Whether an error is a payload that failed its schema: a `ZodError`, or the error `parseOrThrow`
 * throws around one. Its message names fields and Zod issue codes, so it is for Sentry and developers,
 * never for the reader (#7071).
 */
export function isSchemaValidationFailure(error: unknown): boolean {
  return error instanceof ZodError || (error instanceof Error && error.cause instanceof ZodError);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseOrThrow<TSchema extends ZodType<any, any, any>>(
  schema: TSchema,
  input: unknown,
  ctx: {
    schemaName: string;
    operation: string;
    payloadSample: unknown;
    logError: boolean;
  },
): z.output<TSchema> {
  const result = schema.safeParse(input);
  if (result.success) return result.data;

  if (ctx.logError) {
    logZodError(result.error, {
      ...ctx,
      schemaName: ctx.schemaName ?? schema.def.type,
      operation: ctx.operation,
      payloadSample: ctx.payloadSample ?? input,
    });
  }

  const zodError = result.error;
  const pretty = zodError.issues
    .map((issue) => `- ${issue.path.join(".") || "<root>"}: ${issue.message} (${issue.code})`)
    .join("\n");
  const error = new Error(
    ["Zod validation failed", `Schema: ${ctx.schemaName}`, `Operation: ${ctx.operation}`, `Issues:\n${pretty}`]
      .filter(Boolean)
      .join("\n"),
  );

  (error as ZodError).cause = zodError;
  throw error;
}
